/**
 * 全 entry ファイル共通の auto-init ロジック
 * DOM ready 時に TalknWidgets.renderAll() を実行する
 */

const STORAGE_KEY = 'talkn_session_id';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

type TalknClient = {
  retune?: (connection: string) => Promise<unknown>;
  untune?: () => Promise<unknown>;
};

let pageLiveClient: TalknClient | null = null;

function getCurrentConnection(): string {
  const { hostname, pathname, search } = window.location;
  const pathWithSearch = `${pathname}${search}`;
  return `/${hostname}${pathWithSearch}`;
}

function getOrCreateSessionId(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { id: string; exp: number };
      if (parsed.exp > Date.now()) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ id: parsed.id, exp: Date.now() + SESSION_TTL_MS })
        );
        return parsed.id;
      }
    }
  } catch {
    // localStorage unavailable or corrupt — fall through to create new
  }
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, exp: Date.now() + SESSION_TTL_MS }));
  } catch {
    // ignore
  }
  return id;
}

function getTrackBase(): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'https://localhost:9002';
  }
  return 'https://admin.talkn.io';
}

function sendTrackEvent(eventType: 'page_view' | 'route_change', ch: string, sessionId: string): void {
  const base = getTrackBase();
  const body = JSON.stringify({
    sessionId,
    eventType,
    ch,
    url: window.location.href,
    referrer: document.referrer || undefined,
    userAgent: navigator.userAgent,
  });
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(`${base}/api/public/track`, blob);
  } else {
    fetch(`${base}/api/public/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // fire-and-forget
    });
  }
}

function startSessionTracking(): void {
  if ((window as any).TALKN_NO_TRACKING) return;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;

  const sessionId = getOrCreateSessionId();
  const ch = getCurrentConnection();

  sendTrackEvent('page_view', ch, sessionId);

  // SPA route change tracking
  const patchHistory = (method: 'pushState' | 'replaceState') => {
    const original = history[method].bind(history);
    history[method] = function (state: any, title: string, url?: string | null) {
      original(state, title, url);
      sendTrackEvent('route_change', getCurrentConnection(), sessionId);
    };
  };
  patchHistory('pushState');
  patchHistory('replaceState');
  window.addEventListener('popstate', () => {
    sendTrackEvent('route_change', getCurrentConnection(), sessionId);
  });
}

function shouldStartPageLiveConnection(): boolean {
  if ((window as any).TALKN_NO_LIVE) return false;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return false;
  return true;
}

function retunePageLiveConnection(): void {
  if (!pageLiveClient?.retune) return;
  pageLiveClient.retune(getCurrentConnection()).catch(() => {});
}

function startPageLiveConnection(): void {
  if (pageLiveClient || !shouldStartPageLiveConnection()) return;
  const talknAPI = (window as any).TalknAPI;
  if (typeof talknAPI !== 'function') return;

  talknAPI(getCurrentConnection(), { tuneOption: {}, connectionMode: 'tune-shared' })
    .then((client: TalknClient) => {
      pageLiveClient = client;
    })
    .catch(() => {});

  const patchHistory = (method: 'pushState' | 'replaceState') => {
    const original = history[method].bind(history);
    history[method] = function (state: any, title: string, url?: string | null) {
      original(state, title, url);
      setTimeout(retunePageLiveConnection, 0);
    };
  };
  patchHistory('pushState');
  patchHistory('replaceState');
  window.addEventListener('popstate', () => setTimeout(retunePageLiveConnection, 0));
}

export const autoInit = (TalknWidgets: {
  init?: (config?: { host?: string; protocol?: string }) => Promise<void>;
  renderAll: () => Promise<void>;
}): void => {
  if (typeof window === 'undefined' || (window as any).TALKN_NO_AUTO_INIT) return;

  const run = () => {
    startSessionTracking();

    const localConfig =
      window.location.hostname === 'localhost'
        ? { host: 'localhost:10444', protocol: 'https:' }
        : undefined;

    const ready = typeof TalknWidgets.init === 'function'
      ? TalknWidgets.init(localConfig)
      : Promise.resolve();

    ready
      .then(() => TalknWidgets.renderAll())
      .then(() => startPageLiveConnection())
      .catch(error => {
        console.error('TalknWidgets auto-initialization failed:', error);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
};
